import { useMemo } from 'react';
import { getFeatureFlagKeys, resolveRoutesForFeatureFlags } from '@/navigation/routes/routeFlags';
import { useFeatureFlags } from './useFeatureFlags';
import type { AppRouteDefinition } from '@/navigation/routes/routes';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';

type UseAppFeatureFlagsArgs = {
  routes: AppRouteDefinition[];
  userId: string | null;
  organisationId?: string | null;
  extraKeys?: string[];
};

const emptyExtraKeys: string[] = [];

export const useAppFeatureFlags = ({
  routes,
  userId,
  organisationId,
  extraKeys = emptyExtraKeys,
}: UseAppFeatureFlagsArgs): {
  routeDefinitions: AppRouteDefinition[];
  featureFlags: FeatureFlagResolveResult['flags'] | null;
  featureFlagsLoading: boolean;
} => {
  const featureFlagKeys = useMemo(() => getFeatureFlagKeys(routes, extraKeys), [extraKeys, routes]);
  const { flags, isLoading } = useFeatureFlags({
    keys: featureFlagKeys,
    identityKey: userId,
    organisationId,
    enabled: userId !== null,
    refetchOnWindowFocus: false,
  });
  const shouldDeferRouteFiltering = userId === null || isLoading;
  const routeDefinitions = useMemo(
    () => resolveRoutesForFeatureFlags(routes, flags, shouldDeferRouteFiltering),
    [flags, routes, shouldDeferRouteFiltering],
  );
  return { routeDefinitions, featureFlags: flags, featureFlagsLoading: isLoading };
};
